import React, { useState } from 'react';
import { User } from '../types';
import { Users, Briefcase, LogOut, Kanban, FileSpreadsheet, CheckCircle2, BarChart2, Loader2, Sparkles } from 'lucide-react';

const WERFTOOLS_LOGO_URL =
   'https://xquintpfpzdsxupnhypj.supabase.co/storage/v1/object/public/public/website/Werftools.png';
interface ClientPortalLayoutProps {
   children: React.ReactNode;
   user: User;
   onLogout: () => void;
   activeTab: string;
   setActiveTab: (tab: string) => void;
   onPerspectiveChange?: (role: any) => void;
   onOpenChat?: () => void;
   taskbarItems?: any[];
   activeTaskbarId?: string | null;
   onTaskbarClick?: (item: any) => void;
   onRemoveTaskbarItem?: (id: string, e: any) => void;
   contentLoading?: boolean;
   /** Alleen main Layout; clientportaal negeert. */
   engagementCockpitLayout?: boolean;
}

const ClientPortalLayout: React.FC<ClientPortalLayoutProps> = ({ children, user, onLogout, activeTab, setActiveTab, contentLoading }) => {
   const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

   const menuItems = [
      { id: 'engagement_center', label: 'Samenwerking', icon: <Sparkles size={20} /> },
      { id: 'vacancies', label: 'Vacatures', icon: <Briefcase size={20} /> },
      { id: 'pipeline', label: 'Pipeline', icon: <Kanban size={20} /> },
      { id: 'shortlist', label: 'Shortlist', icon: <FileSpreadsheet size={20} /> },
      { id: 'talentpool', label: 'Kandidaten', icon: <Users size={20} /> },
      { id: 'placements', label: 'Plaatsingen', icon: <CheckCircle2 size={20} /> },
      { id: 'reporting', label: 'Rapportage', icon: <BarChart2 size={20} /> }
   ]
      .filter((item) => Boolean(user.visibleTabs?.includes(item.id)));

   const clientLogo = (user.clientLogoUrl && String(user.clientLogoUrl).trim()) || '';
   const clientAlt = user.clientName ? `${user.clientName}` : 'Jouw organisatie';

   /** Werftools (platformbeeld) + klantlogo naast elkaar. */
   const BrandCluster = ({ compact }: { compact?: boolean }) => (
      <div
         className={
            compact
               ? 'flex items-center gap-2 min-w-0'
               : 'flex flex-row items-center justify-center gap-2.5 sm:gap-3 w-full min-h-0 flex-wrap px-0.5'
         }
      >
         <img
            src={WERFTOOLS_LOGO_URL}
            alt="Werftools"
            className={
               compact
                  ? 'h-7 w-auto max-h-7 object-contain object-left shrink-0'
                  : 'h-9 sm:h-10 w-auto max-w-[min(100%,10rem)] object-contain shrink-0'
            }
         />
         <div className={`w-px shrink-0 bg-slate-200 ${compact ? 'h-6 self-center' : 'h-8 sm:h-9 self-center'}`} aria-hidden />
         {clientLogo ? (
            <img
               src={clientLogo}
               alt={clientAlt}
               className={
                  compact
                     ? 'h-8 w-auto max-w-[min(40vw,9rem)] object-contain object-left'
                     : 'max-h-10 sm:max-h-11 w-auto max-w-[min(100%,10rem)] object-contain'
               }
            />
         ) : (
            <div
               className={
                  compact
                     ? 'rounded-md bg-slate-100 px-2 py-1 max-w-[min(40vw,9rem)] truncate'
                     : 'rounded-lg bg-slate-50 border border-slate-100 px-2 py-1.5 text-center max-w-[min(100%,10rem)]'
               }
               title={user.clientName || undefined}
            >
               <span className="text-[10px] font-bold text-slate-500 leading-tight line-clamp-2">
                  {user.clientName || 'Organisatie'}
               </span>
            </div>
         )}
      </div>
   );

   return (
      <div className="flex w-full h-[100dvh] bg-[#F8FAFC] font-sans text-slate-900 overflow-hidden">
         {/* DESKTOP SIDEBAR */}
         <aside className="hidden lg:flex flex-col m-1 rounded-xl shadow-sm z-20 w-60 bg-white border border-gray-100">
            <div className="min-h-[5.5rem] flex items-center justify-center p-3 border-b border-slate-100">
               <BrandCluster />
            </div>
            
            <div className="px-4 pb-4">
               <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 pl-2">Portal</p>
            </div>

            <nav className="flex-1 px-3 space-y-2 overflow-y-auto">
               {menuItems.map(item => (
                  <button
                     key={item.id}
                     onClick={() => setActiveTab(item.id)}
                     className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all font-bold text-sm ${activeTab === item.id ? 'bg-indigo-50 text-indigo-600 shadow-sm' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'}`}
                  >
                     {item.icon}
                     {item.label}
                  </button>
               ))}
            </nav>

            {/* Profile Footer */}
            <div className="p-4 mt-auto border-t border-slate-100">
               <div className="flex items-center gap-3 p-2 bg-slate-50 rounded-xl mb-3">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center font-black text-indigo-600 shadow-sm shrink-0 border border-slate-100">
                     {user.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                     <p className="text-xs font-bold text-slate-900 truncate">{user.name}</p>
                     <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest truncate">Opdrachtgever</p>
                  </div>
               </div>
               <button onClick={onLogout} className="w-full flex items-center justify-center gap-2 p-2.5 rounded-xl text-red-500 hover:bg-red-50 font-bold text-xs uppercase tracking-wide transition-colors">
                  <LogOut size={16} /> Uitloggen
               </button>
            </div>
         </aside>

         {/* MOBILE HEADER */}
         <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-100 flex justify-between items-center px-3 gap-2 z-50">
            <BrandCluster compact />
            <div className="flex items-center gap-2">
               <button onClick={onLogout} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                  <LogOut size={20} />
               </button>
            </div>
         </header>

         {/* MOBILE NAV */}
         <nav
            className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 pb-safe z-50 h-16 shadow-[0_-10px_40px_rgba(0,0,0,0.05)] overflow-x-auto overflow-y-hidden"
            aria-label="Hoofdnavigatie"
         >
            <div
               className={`h-full flex items-stretch px-2 ${
                  menuItems.length <= 5 ? 'justify-around' : 'justify-start gap-1'
               }`}
               style={menuItems.length > 5 ? { minWidth: 'max-content' } : undefined}
            >
               {menuItems.map(item => {
                  const isActive = activeTab === item.id;
                  return (
                     <button
                        key={item.id}
                        onClick={() => setActiveTab(item.id)}
                        className={`flex flex-col items-center justify-center gap-1 px-3 min-w-[64px] shrink-0 transition-colors ${
                           isActive ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'
                        }`}
                        aria-current={isActive ? 'page' : undefined}
                     >
                        {React.cloneElement(item.icon as React.ReactElement, {
                           size: isActive ? 22 : 20,
                        })}
                        <span className="text-[9px] font-bold leading-tight text-center max-w-[64px] truncate">
                           {item.label}
                        </span>
                     </button>
                  );
               })}
            </div>
         </nav>

         {/* MAIN CONTENT AREA */}
         <main className="flex-1 relative lg:m-1 lg:rounded-xl lg:border lg:border-slate-200 lg:shadow-sm bg-white overflow-hidden flex flex-col pt-16 lg:pt-0 pb-16 lg:pb-0">
            {contentLoading ? (
               <div className="absolute inset-0 z-[80] flex flex-col items-center justify-center bg-white/90 backdrop-blur-sm">
                  <Loader2 className="h-10 w-10 animate-spin text-indigo-600 mb-3" aria-hidden />
                  <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">Gegevens laden…</p>
               </div>
            ) : null}
            <div className="flex-1 overflow-y-auto w-full h-full">
               {children}
            </div>
         </main>
      </div>
   );
};

export default ClientPortalLayout;
